
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  watch,
  defineComponent,
  PropType,
} from 'vue';
import DataTable from 'primevue/datatable';
import type {
  DataTablePageEvent,
} from 'primevue/datatable';
import Column from 'primevue/column';
import Skeleton from 'primevue/skeleton';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Menu from 'primevue/menu';
import Badge from 'primevue/badge';
import Dialog from 'primevue/dialog';
import { useI18n } from 'vue-i18n';
import Loader from '@/components/layout/Loader.vue';
import { nestCall } from '@/composables/nestApi';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import SpmPanelMenu from '@/components/spm-primevue/SpmPanelMenu.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import RenderDatetime from './renderers/RenderDatetime.vue';
import SpmTableApiControlsPanel from './SpmTableApiControlsPanel.vue';

import type {
  StandardQueryListing,
  SpmColumnConfig,
  ApiCallConfig,
  GroupedActionOption,
  RowActionOption,
  SpmApiFilter,
  SpmActiveSort,
  ExportConfig,
  ExportPayload,
} from './types';
import {
  applyClientFilters,
  applyClientSort,
  applyClientPagination,
} from './clientMode';
import {
  extractApiFields,
  getFieldConfig,
  getFieldName,
  getSortableFields,
  getFilterableFields,
} from './types';
import type { SpmActiveFilter } from './filters';
import { buildApiFilters } from './filters';

interface Props {
  /* Data mode: 'server' (default) fetches from API, 'client' uses provided data, 'dynamic' fetches columns + data */
  mode?: 'server' | 'client' | 'dynamic';
  /* API configuration - required for server mode */
  apiCall?: ApiCallConfig;
  /* Client-side data - required for client mode */
  data?: any[];
  /* Dynamic fetch function - required for dynamic mode.
   * Returns { columns, data, total }.
   * En mode pagination optimisée (back ne calcule pas le COUNT exact), peut
   * aussi renvoyer `hasMore: true` + `sortingDisabled: true` ; le composant
   * bascule alors son paginator en prev/next et masque "{total}" dans le
   * report de page. Cf. stats `/stats/widget-data` avec `includeCount: false`. */
  dynamicFetchFn?: (params: any) => Promise<{
    columns: any[];
    data: any[];
    total: number;
    hasMore?: boolean;
    sortingDisabled?: boolean;
  }>;
  columns: SpmColumnConfig[];
  /* default primevue datatable props */
  dataKey?: string | string[];
  initialRows?: number;
  rowsPerPageOptions?: number[];
  showGridlines?: boolean;
  stripedRows?: boolean;
  size?: 'small' | 'normal' | 'large';
  rowHover?: boolean;
  /* end default primevue datatable props */
  showReloadButton?: boolean;
  expandable?: boolean;
  isRowExpandable?: (row: any) => boolean;
  enableGroupedActions?: boolean;
  groupedActionsOptions?: GroupedActionOption[];
  rowActionsOptions?: RowActionOption[];
  /* Filters & Sorting props */
  persistentFilters?: SpmApiFilter[];
  initialSort?: SpmActiveSort | null;
  showFiltersSortButton?: boolean;
  /* Client mode reload function */
  clientReloadFn?: () => Promise<any[]>;
  /* Deferred columns (columns flagged `lazy: true`) — server mode only.
   * Called with the rows of one chunk, must resolve to a map
   * `rowKey -> fields to merge into the row` (or null to skip). */
  lazyLoadFn?: (rows: any[]) => Promise<Record<string, Record<string, unknown>> | null>;
  /* Chunk size of the sequential lazy loading (progressive fill) */
  lazyChunkSize?: number;
  /* Silent re-fetch of the lazy fields every N ms (0 = disabled) */
  lazyRefreshInterval?: number;
  /* Export props */
  exportable?: boolean;
  showExportAllButton?: boolean;
  exportConfig?: ExportConfig;
  /* Row class function (pass-through to DataTable) */
  rowClass?: (data: any) => string | object;
}

export default defineComponent({
  name: 'SpmTableAPI',
  components: {
    DataTable,
    Loader,
    Column,
    Button,
    RenderDatetime,
    Checkbox,
    Menu,
    Badge,
    Dialog,
    SpmOverlayPanel,
    SpmPanelMenu,
    SpmButton,
    SpmTableApiControlsPanel,
    Skeleton,
  },

  props: {
    mode: { type: String as PropType<'server' | 'client' | 'dynamic'>, default: 'server' },
    apiCall: { type: Object as PropType<ApiCallConfig>, default: undefined },
    data: { type: Array as PropType<any[]>, default: () => [] },
    dynamicFetchFn: { type: Function as PropType<(params: any) => Promise<{ columns: any[]; data: any[]; total: number; hasMore?: boolean; sortingDisabled?: boolean }>>, default: null },
    columns: { type: Array as PropType<SpmColumnConfig[]>, required: true },

    dataKey: { type: [String, Array] as PropType<string | string[]>, default: 'id' },
    initialRows: { type: Number as PropType<number>, default: 10 },
    rowsPerPageOptions: {
      type: Array as PropType<number[]>,
      default: () => [10, 20, 50],
    },

    showGridlines: { type: Boolean as PropType<boolean>, default: false },
    stripedRows: { type: Boolean as PropType<boolean>, default: false },
    size: { type: String as PropType<'small' | 'normal' | 'large'>, default: 'normal' },
    rowHover: { type: Boolean as PropType<boolean>, default: true },
    showReloadButton: { type: Boolean as PropType<boolean>, default: false },
    expandable: { type: Boolean as PropType<boolean>, default: false },
    isRowExpandable: { type: Function as PropType<(row: any) => boolean>, default: null },

    // Grouped actions props
    enableGroupedActions: { type: Boolean as PropType<boolean>, default: false },
    groupedActionsOptions: { type: Array as PropType<GroupedActionOption[]>, default: () => [] },

    // Row actions props
    rowActionsOptions: { type: Array as PropType<RowActionOption[]>, default: () => [] },

    // Filters & Sorting props
    persistentFilters: { type: Array as PropType<SpmApiFilter[]>, default: () => [] },
    initialSort: { type: Object as PropType<SpmActiveSort | null>, default: null },
    showFiltersSortButton: { type: Boolean as PropType<boolean>, default: true },

    // Client mode reload function
    clientReloadFn: { type: Function as PropType<() => Promise<any[]>>, default: null },

    // Deferred columns (lazy: true) — see interface Props
    lazyLoadFn: { type: Function as PropType<(rows: any[]) => Promise<Record<string, Record<string, unknown>> | null>>, default: null },
    lazyChunkSize: { type: Number as PropType<number>, default: 5 },
    lazyRefreshInterval: { type: Number as PropType<number>, default: 0 },

    // Export props
    exportable: { type: Boolean as PropType<boolean>, default: false },
    showExportAllButton: { type: Boolean as PropType<boolean>, default: true },
    exportConfig: { type: Object as PropType<ExportConfig>, default: () => ({ entityType: '' }) },

    // Row class function (pass-through to DataTable)
    rowClass: { type: Function as PropType<(data: any) => string | object>, default: null },
  },

  emits: ['export', 'reload', 'row-click', 'data-loaded'],

  setup(props: Props, { expose, emit }) {
    const {
      t,
    } = useI18n();
    const dt = ref();
    const records = ref<any[]>([]);
    const totalRecords = ref(0);
    const loading = ref(false);
    const expandedRows = ref<any[]>([]);

    // Mode pagination optimisée : remontés depuis `dynamicFetchFn` quand le
    // back ne calcule pas le COUNT exact (cf. /stats/widget-data avec
    // includeCount: false ou auto-bascule au-delà de 50000 lignes).
    const hasMore = ref(false);
    const sortingDisabled = ref(false);

    // Dynamic columns state (for dynamic mode)
    const dynamicColumns = ref<SpmColumnConfig[]>([]);

    // Effective columns: use dynamic columns if available, otherwise use props.columns
    const effectiveColumns = computed(() => {
      if (props.mode === 'dynamic' && dynamicColumns.value.length > 0) {
        return dynamicColumns.value;
      }
      return props.columns;
    });

    // Table CSS classes
    const tableClasses = computed(() => ({
      'spm-table-apiV2': true,
      'spm-table-no-row-hover': !props.rowHover,
    }));

    const lazyParams = ref<{
      first: number;
      rows: number;
    }>({
      first: 0,
      rows: props.initialRows ?? 10,
    });

    // Computed dataKey with fallback (can be string or array of strings)
    const dataKey = computed(() => props.dataKey || 'id');
    const tableDataKey = computed(() => (Array.isArray(dataKey.value) ? dataKey.value[0] : dataKey.value));

    // Paginator adaptatif : en mode pagination optimisée (hasMore actif),
    // on retire les numéros de page + First/Last et on masque "{total}" du
    // report (qui serait un total artificiellement gonflé, donc faux).
    const paginatorTemplate = computed(() => {
      if (hasMore.value) {
        return 'PrevPageLink NextPageLink CurrentPageReport RowsPerPageDropdown';
      }
      return 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown';
    });

    const currentPageReportTemplate = computed(() => {
      if (hasMore.value) {
        return `${t('results')} {first} ${t('to')} {last}`;
      }
      return `${t('results')} {first} ${t('to')} {last} ${t('of')} {totalRecords}`;
    });

    /**
     * Generate a unique row key from the dataKey field(s)
     * For composite keys, joins values with comma separator
     * @param rowData - The row data object
     * @returns A unique string key for the row
     */
    const getRowKey = (rowData: any): string => {
      const key = dataKey.value;
      if (Array.isArray(key)) {
        // Composite key: join values with comma
        return key.map((k) => rowData[k]).join(',');
      }
      // Simple key: return the value as string
      return String(rowData[key] ?? '');
    };

    // Grouped actions state (using string keys to support composite keys)
    const selectedRows = ref<string[]>([]);
    const selectAllMode = ref(false); // true = all records selected, false = only specific IDs
    const excludedRows = ref<string[]>([]); // Keys to exclude when in select all mode
    const menu = ref();
    const headerMenu = ref();

    // State to close row actions overlay panel
    const closeRowActionsPanel = ref(false);

    // Row actions state (no longer needed with SpmOverlayPanel)

    // Computed for selection count (displayed in badge)
    const selectionCount = computed(() => {
      if (selectAllMode.value) {
        return totalRecords.value - excludedRows.value.length;
      }
      return selectedRows.value.length;
    });

    // Visible columns (exclude hidden columns used for expandable data)
    const visibleColumns = computed(() => effectiveColumns.value.filter((col) => !col.hidden));

    // All API fields to fetch (deduplicated, supports string | SpmFieldConfig)
    const apiFields = computed(() => extractApiFields(effectiveColumns.value).join(','));

    // Controls panel (filters & sort sidebar)
    const showControlsPanel = ref(false);
    const sortableFields = computed(() => getSortableFields(effectiveColumns.value));
    const filterableFields = computed(() => getFilterableFields(effectiveColumns.value));
    const hasFiltersOrSort = computed(() => sortableFields.value.length > 0 || filterableFields.value.length > 0);

    // Active filters state (user-defined filters from controls panel)
    const activeFilters = ref<Map<string, SpmActiveFilter>>(new Map());

    // Active sort state
    const activeSort = ref<SpmActiveSort | null>(props.initialSort ?? null);

    // Track if user has modified sort (to distinguish from initialSort)
    const userModifiedSort = ref(false);

    // Computed: build API filters from active filters + persistent filters
    const apiFilters = computed(() => buildApiFilters(
      Array.from(activeFilters.value.values()),
      props.persistentFilters,
    ));

    // Computed: build API order from active sort.
    // En mode pagination optimisée, le back retire l'ORDER BY — on ne
    // transmet donc pas le tri pour éviter un échange inutile et un état
    // fantôme. Le `activeSort` reste en mémoire et reprend effet dès que
    // le mode standard est rétabli.
    const apiOrder = computed(() => {
      if (sortingDisabled.value) return [];
      if (activeSort.value) {
        return [{ field: activeSort.value.field, type: activeSort.value.order }];
      }
      return [];
    });

    // Total count of active controls (filters + user-modified sort)
    // Note: initialSort is NOT counted, only user-applied sort. Sort
    // exclu du compteur quand `sortingDisabled` car visuellement inerte.
    const activeControlsCount = computed(() => {
      let count = activeFilters.value.size;
      if (!sortingDisabled.value && userModifiedSort.value && activeSort.value) {
        count += 1;
      }
      return count;
    });

    // Check if there are any user-applied filters or sort (for reset button visibility)
    const hasUserControls = computed(() => activeControlsCount.value > 0);

    // Button class: success when filters/sort are active
    const filtersButtonClass = computed(() => (activeControlsCount.value > 0
      ? 'p-button p-button-success'
      : 'p-button p-button-secondary'));

    // Badge text: show count when active
    const filtersButtonBadge = computed(() => (activeControlsCount.value > 0
      ? String(activeControlsCount.value)
      : ''));

    // Computed state for header checkbox (3 states: unchecked, indeterminate, checked)
    const headerCheckboxState = computed(() => {
      // Calculate total selected count
      const totalSelectedCount = selectAllMode.value
        ? totalRecords.value - excludedRows.value.length
        : selectedRows.value.length;

      // No selection at all
      if (totalSelectedCount === 0) {
        return { checked: false, indeterminate: false };
      }

      // All records in the entire datatable are selected
      if (totalSelectedCount === totalRecords.value) {
        return { checked: true, indeterminate: false };
      }

      // Some records selected but not all (indeterminate state with dash)
      return { checked: false, indeterminate: true };
    });

    const getRendererForDataType = (dataType: string | undefined) => {
      if (dataType === 'datetime') {
        return RenderDatetime;
      }
      // Add other dataTypes and their renderers here
      // e.g., if (dataType === 'currency') return RenderCurrency;
      return null; // No specific renderer for this dataType
    };

    /**
     * Toggle row expansion (for conditional expandable rows)
     */
    const toggleRowExpansion = (row: Record<string, unknown>) => {
      const key = getRowKey(row);
      const index = expandedRows.value.findIndex((r: any) => getRowKey(r) === key);
      if (index >= 0) {
        expandedRows.value = expandedRows.value.filter((_: any, i: number) => i !== index);
      } else {
        expandedRows.value = [...expandedRows.value, row];
      }
    };

    /**
     * Check if a row is expanded
     */
    const isRowExpanded = (row: Record<string, unknown>): boolean => {
      const key = getRowKey(row);
      return expandedRows.value.some((r: any) => getRowKey(r) === key);
    };

    // ---- Deferred columns (columns flagged `lazy: true`) -------------------
    // The listing renders first with its base fields; the lazy columns are
    // then filled chunk by chunk through `lazyLoadFn`, and optionally
    // re-fetched silently every `lazyRefreshInterval` ms.

    const hasLazyColumns = computed(() => effectiveColumns.value.some((col) => col.lazy));

    // Row keys whose lazy fields have not landed yet (drives the skeletons).
    const lazyPendingKeys = ref<Set<string>>(new Set());

    // Bumped on every primary load: a chunk response or a refresh tick that
    // does not carry the current id is stale and must be dropped.
    let lazyRequestId = 0;
    let lazyRefreshTimer: ReturnType<typeof setTimeout> | null = null;

    const clearLazyRefreshTimer = () => {
      if (lazyRefreshTimer !== null) {
        clearTimeout(lazyRefreshTimer);
        lazyRefreshTimer = null;
      }
    };

    // One skeleton line per labelled metric, so the placeholder matches the
    // height of the rendered cell.
    const lazySkeletonRows = (col: SpmColumnConfig): number => {
      const labelled = col.fields.filter((field) => getFieldConfig(field).label);
      return labelled.length || 1;
    };

    const isRowLazyPending = (row: Record<string, unknown>): boolean => lazyPendingKeys.value.has(getRowKey(row));

    /**
     * Fetch the lazy fields of the current rows, `lazyChunkSize` rows at a
     * time, merging each chunk into the displayed records as soon as it
     * lands (progressive fill). `silent` skips the skeletons — used by the
     * periodic refresh so the table never flickers back to placeholders.
     * Never awaited by the caller: a slow or failing enrichment must not
     * delay or blank out the listing itself.
     */
    const runLazyCycle = async (requestId: number, silent: boolean) => {
      const { lazyLoadFn } = props;
      if (!lazyLoadFn) {
        return;
      }

      // Arms the next silent refresh tick for this same page of rows.
      const armNextRefresh = () => {
        clearLazyRefreshTimer();
        if (!props.lazyRefreshInterval || props.lazyRefreshInterval <= 0) {
          return;
        }
        lazyRefreshTimer = setTimeout(() => {
          if (requestId !== lazyRequestId) {
            return;
          }
          runLazyCycle(requestId, true);
        }, props.lazyRefreshInterval);
      };

      // Tab in background: skip this refresh, try again next tick.
      if (silent && typeof document !== 'undefined' && document.hidden) {
        armNextRefresh();
        return;
      }

      const rows = records.value as Record<string, unknown>[];

      if (!silent) {
        lazyPendingKeys.value = new Set(rows.map((row) => getRowKey(row)).filter(Boolean));
      }

      const chunkSize = Math.max(1, props.lazyChunkSize ?? 5);
      for (let start = 0; start < rows.length; start += chunkSize) {
        if (requestId !== lazyRequestId) {
          return;
        }
        const chunk = rows.slice(start, start + chunkSize);

        try {
          // eslint-disable-next-line no-await-in-loop
          const byKey = await lazyLoadFn(chunk);

          if (requestId !== lazyRequestId) {
            return;
          }

          if (byKey) {
            records.value = records.value.map((row: Record<string, unknown>) => {
              const extra = byKey[getRowKey(row)];
              return extra ? { ...row, ...extra } : row;
            });
          }
        } catch (error) {
          // The listing is already displayed: a failed enrichment must not
          // blank it out. The affected rows keep their previous values.
          console.error('Lazy fields request failed:', error);
        } finally {
          if (requestId === lazyRequestId && !silent) {
            const next = new Set(lazyPendingKeys.value);
            chunk.forEach((row) => next.delete(getRowKey(row)));
            lazyPendingKeys.value = next;
          }
        }
      }

      armNextRefresh();
    };

    const startLazyCycleIfNeeded = () => {
      if (!props.lazyLoadFn || !hasLazyColumns.value || records.value.length === 0) {
        lazyPendingKeys.value = new Set();
        return;
      }
      runLazyCycle(lazyRequestId, false);
    };

    onUnmounted(() => {
      // Invalidate any in-flight cycle so it can neither merge nor re-arm the
      // refresh timer after the component is gone.
      lazyRequestId += 1;
      clearLazyRefreshTimer();
    });

    /**
     * Load data from server API (server mode)
     */
    const loadServerData = async () => {
      if (!props.apiCall) {
        console.error('SpmTableAPI: apiCall is required in server mode');
        return;
      }

      // Any in-flight lazy chunk or armed refresh now belongs to a stale page.
      lazyRequestId += 1;
      clearLazyRefreshTimer();

      const query: StandardQueryListing = {
        offset: lazyParams.value.first,
        limit: lazyParams.value.rows,
        fields: apiFields.value,
        // Add filters if any
        ...(apiFilters.value.length > 0 && { filters: apiFilters.value }),
        // Add order if any
        ...(apiOrder.value.length > 0 && { order: apiOrder.value }),
      };

      try {
        const response = await nestCall(
          'get',
          props.apiCall.type_api,
          props.apiCall.path,
          {},
          '',
          undefined,
          props.apiCall.secure,
          query,
        );

        if (response && response.data) {
          records.value = response.data;
          totalRecords.value = response.meta?.pagination?.total_items ?? 0;
          startLazyCycleIfNeeded();
        } else if (response && response.error) {
          console.error('API Error:', response.error);
          records.value = [];
          totalRecords.value = 0;
          lazyPendingKeys.value = new Set();
        } else {
          records.value = [];
          totalRecords.value = 0;
          lazyPendingKeys.value = new Set();
        }
      } catch (error) {
        console.error('Request failed:', error);
        records.value = [];
        totalRecords.value = 0;
        lazyPendingKeys.value = new Set();
      }
    };

    /**
     * Process data locally (client mode)
     * Applies filters, sorting, and pagination on the provided data
     */
    const loadClientData = () => {
      if (!props.data || props.data.length === 0) {
        records.value = [];
        totalRecords.value = 0;
        return;
      }

      // Convert Map to Array for client-side filtering
      const filtersArray = Array.from(activeFilters.value.values());

      // Apply filters
      let processedData = applyClientFilters(props.data, filtersArray);

      // Apply sorting
      processedData = applyClientSort(processedData, activeSort.value);

      // Store total count before pagination
      totalRecords.value = processedData.length;

      // Apply pagination
      records.value = applyClientPagination(
        processedData,
        lazyParams.value.first,
        lazyParams.value.rows,
      );
    };

    /**
     * Load data and columns dynamically (dynamic mode)
     * Fetches both columns and data from the provided function
     */
    const loadDynamicData = async () => {
      if (!props.dynamicFetchFn) {
        return;
      }

      try {
        const params = {
          offset: lazyParams.value.first,
          limit: lazyParams.value.rows,
          filters: apiFilters.value,
          order: apiOrder.value,
        };

        const response = await props.dynamicFetchFn(params);

        if (response) {
          // Update columns if provided
          if (response.columns && Array.isArray(response.columns)) {
            dynamicColumns.value = response.columns;
          }

          // Update data
          records.value = response.data || [];
          totalRecords.value = response.total || 0;
          hasMore.value = Boolean(response.hasMore);
          sortingDisabled.value = Boolean(response.sortingDisabled);
        } else {
          records.value = [];
          totalRecords.value = 0;
          hasMore.value = false;
          sortingDisabled.value = false;
        }
      } catch (error) {
        console.error('Dynamic fetch failed:', error);
        records.value = [];
        totalRecords.value = 0;
        hasMore.value = false;
        sortingDisabled.value = false;
      }
    };

    /**
     * Main data loading function - handles server, client, and dynamic modes
     */
    const loadLazyData = async (event?: Partial<DataTablePageEvent>) => {
      loading.value = true;

      if (event) {
        // Update pagination params
        lazyParams.value = {
          ...lazyParams.value,
          first: event.first ?? lazyParams.value.first,
          rows: event.rows ?? lazyParams.value.rows,
        };
      }

      try {
        if (props.mode === 'client') {
          loadClientData();
        } else if (props.mode === 'dynamic') {
          await loadDynamicData();
        } else {
          await loadServerData();
        }
      } finally {
        loading.value = false;
      }
    };

    const onPage = (event: DataTablePageEvent) => {
      lazyParams.value.first = event.first;
      lazyParams.value.rows = event.rows;
      loadLazyData();
    };

    /**
     * Reload data - handles server, client, and dynamic modes
     * In client mode with clientReloadFn, calls the function which should update the data prop
     * In client mode without clientReloadFn, emits 'reload' event for parent to handle
     * In dynamic mode, reloads from dynamicFetchFn
     */
    const reloadData = async () => {
      if (props.mode === 'client') {
        if (props.clientReloadFn) {
          loading.value = true;
          try {
            // Call the reload function - it should update the data prop in parent
            await props.clientReloadFn();
            // The watcher on props.data will trigger loadClientData automatically
          } finally {
            loading.value = false;
          }
        } else {
          // Emit reload event for parent to handle manually
          emit('reload');
        }
      } else {
        // Server or dynamic mode: reload from API
        loadLazyData();
      }
    };

    // Grouped actions functions
    const selectVisible = () => {
      // Replace current selection with only visible rows
      const visibleKeys = records.value.map((item: any) => getRowKey(item));
      selectedRows.value = visibleKeys;
      selectAllMode.value = false;
      excludedRows.value = [];
    };

    const selectAll = () => {
      // Select ALL records (not just visible ones)
      selectAllMode.value = true;
      selectedRows.value = []; // Clear specific IDs when in "select all" mode
      excludedRows.value = []; // Clear exclusions
    };

    const unselectAll = () => {
      selectedRows.value = [];
      selectAllMode.value = false;
      excludedRows.value = [];
    };

    const onHeaderCheckboxClick = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      if (headerMenu.value) {
        headerMenu.value.toggle(event);
      }
    };

    const isRowSelected = (rowKey: string) => {
      if (selectAllMode.value) {
        return !excludedRows.value.includes(rowKey); // Selected unless excluded
      }
      return selectedRows.value.includes(rowKey);
    };

    const toggleRowSelection = (rowKey: string, isSelected: boolean) => {
      if (selectAllMode.value) {
        // In select all mode, manage exclusions
        if (isSelected) {
          // Re-selecting a previously excluded row
          excludedRows.value = excludedRows.value.filter((key) => key !== rowKey);
        } else if (!excludedRows.value.includes(rowKey)) {
          // Excluding a row from select all
          excludedRows.value.push(rowKey);
        }
        return;
      }

      if (isSelected) {
        // Add to selection
        if (!selectedRows.value.includes(rowKey)) {
          selectedRows.value.push(rowKey);
        }
      } else {
        // Remove from selection
        selectedRows.value = selectedRows.value.filter((key) => key !== rowKey);
      }
    };

    const onRowSelectionUpdate = (rowKey: string, isSelected: boolean | undefined) => {
      toggleRowSelection(rowKey, !!isSelected);
    };

    const toggle = (event: Event) => {
      if (menu.value) {
        menu.value.toggle(event);
      }
    };

    // Row actions functions - Get visible actions for a specific row
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getVisibleRowActions = (rowData: any) => {
      if (!props.rowActionsOptions) {
        return [];
      }

      return props.rowActionsOptions.filter((action) => {
        // Check visibility condition
        if (action.visible && !action.visible(rowData)) {
          return false;
        }
        return true;
      });
    };

    // Get row actions formatted for SpmPanelMenu
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getRowActionsForMenu = (rowData: any) => {
      const visibleActions = getVisibleRowActions(rowData);

      return visibleActions.map((action) => {
        // Handle separator
        if (action.separator) {
          return { separator: true };
        }

        return {
          label: action.label,
          icon: action.icon,
          class: action.class,
          disabled: action.disabled ? action.disabled(rowData) : false,
          callback: (data: any, event: any) => {
            // Call the command with the full row data, the row key, and the event
            action.command(rowData, getRowKey(rowData), event);
          },
        };
      });
    };

    // ========== Filters & Sort Management ==========

    /**
     * Update a single filter
     */
    const setFilter = (field: string, filter: SpmActiveFilter) => {
      activeFilters.value.set(field, filter);
    };

    /**
     * Remove a single filter
     */
    const removeFilter = (field: string) => {
      activeFilters.value.delete(field);
    };

    /**
     * Clear all user filters
     */
    const clearFilters = () => {
      activeFilters.value.clear();
    };

    /**
     * Set the active sort
     */
    const setSort = (sort: SpmActiveSort | null) => {
      activeSort.value = sort;
    };

    /**
     * Apply filters and sort from controls panel
     * Resets pagination to first page and reloads data
     */
    const applyFiltersAndSort = (filters: Map<string, SpmActiveFilter>, sort: SpmActiveSort | null) => {
      activeFilters.value = new Map(filters);
      activeSort.value = sort;
      // Mark sort as user-modified if it differs from initialSort
      const initialSortStr = JSON.stringify(props.initialSort);
      const newSortStr = JSON.stringify(sort);
      userModifiedSort.value = initialSortStr !== newSortStr;
      lazyParams.value.first = 0; // Reset to first page
      loadLazyData();
    };

    /**
     * Reset all filters and sort to initial state
     */
    const resetFiltersAndSort = () => {
      activeFilters.value.clear();
      activeSort.value = props.initialSort ?? null;
      userModifiedSort.value = false; // Reset user modification flag
      lazyParams.value.first = 0;
      loadLazyData();
    };

    /**
     * Quick reset from header button (without opening panel)
     */
    const quickResetFiltersAndSort = () => {
      resetFiltersAndSort();
    };

    // ========== End Filters & Sort Management ==========

    // ========== Export Management ==========

    // Export confirmation dialog state
    const showExportConfirmDialog = ref(false);

    /**
     * Handle export all action (button click)
     * Opens confirmation dialog before exporting
     */
    const handleExportAll = () => {
      showExportConfirmDialog.value = true;
    };

    /**
     * Confirm export all action
     * selectAllMode = true, no selectedIds, no filters
     */
    const confirmExportAll = () => {
      const payload: ExportPayload = {
        selectAllMode: true,
        selectedIds: [],
        totalCount: totalRecords.value,
        exportConfig: props.exportConfig ?? { entityType: '' },
      };
      emit('export', payload);
      showExportConfirmDialog.value = false;
    };

    /**
     * Handle export selection action (grouped action)
     * - If selectAllMode: selectedIds = excludedRows (exclusions), include filters
     * - Otherwise: selectedIds = selectedRows (manual selection)
     */
    const handleExportSelection = () => {
      const payload: ExportPayload = {
        selectAllMode: selectAllMode.value,
        selectedIds: selectAllMode.value ? excludedRows.value : selectedRows.value,
        filters: selectAllMode.value ? apiFilters.value : undefined,
        totalCount: selectionCount.value,
        exportConfig: props.exportConfig ?? { entityType: '' },
      };
      emit('export', payload);
    };

    // ========== End Export Management ==========

    // Watch for apiCall changes (server mode)
    watch(
      () => props.apiCall,
      () => {
        if (props.mode === 'server') {
          lazyParams.value.first = 0;
          loadLazyData();
        }
      },
      { deep: true },
    );

    // Watch for persistent filters changes (e.g., data source selector)
    watch(
      () => props.persistentFilters,
      () => {
        lazyParams.value.first = 0;
        loadLazyData();
      },
      { deep: true },
    );

    // Watch for data changes (client mode)
    watch(
      () => props.data,
      () => {
        if (props.mode === 'client') {
          lazyParams.value.first = 0;
          loadLazyData();
        }
      },
      { deep: true },
    );

    // Emit data-loaded whenever the displayed records change
    watch(
      () => records.value,
      (newRecords) => {
        emit('data-loaded', newRecords);
      },
      { deep: false },
    );

    onMounted(() => {
      loadLazyData();
    });

    /**
     * Set loading state externally (for parent components to trigger loading during actions)
     * When enabling loading, closes any open row action overlay panels
     */
    const setLoading = async (value: boolean) => {
      closeRowActionsPanel.value = value;
      loading.value = value;
    };

    expose({
      reloadData,
      dt,
      unselectAll,
      // Filters & Sort
      setFilter,
      removeFilter,
      clearFilters,
      setSort,
      applyFiltersAndSort,
      resetFiltersAndSort,
      activeFilters,
      activeSort,
      // Loading state
      setLoading,
    });

    return {
      dt,
      records,
      totalRecords,
      hasMore,
      sortingDisabled,
      paginatorTemplate,
      currentPageReportTemplate,
      loading,
      lazyParams,
      tableDataKey,
      expandedRows,
      toggleRowExpansion,
      isRowExpanded,
      props,
      onPage,
      reloadData,
      t,
      visibleColumns,
      getFieldName,
      getRowKey,
      // Deferred columns (lazy)
      hasLazyColumns,
      isRowLazyPending,
      lazySkeletonRows,
      // Grouped actions
      selectionCount,
      menu,
      headerMenu,
      headerCheckboxState,

      // Row actions
      getVisibleRowActions,
      getRowActionsForMenu,
      closeRowActionsPanel,

      // Controls panel (filters & sort)
      showControlsPanel,
      hasFiltersOrSort,
      hasUserControls,
      sortableFields,
      filterableFields,
      activeFilters,
      activeSort,
      filtersButtonClass,
      filtersButtonBadge,
      applyFiltersAndSort,
      resetFiltersAndSort,
      quickResetFiltersAndSort,

      headerMenuItems: computed(() => {
        const items = [
          {
            label: t('bulkActions.selection.visible'),
            icon: 'fa-solid fa-eye',
            command: () => selectVisible(),
          },
          {
            label: t('bulkActions.selection.all'),
            icon: 'fa-solid fa-check-double',
            command: () => selectAll(),
          },
        ];

        // Add "Unselect" option if at least one row is selected
        if (selectionCount.value > 0) {
          items.unshift({
            label: t('bulkActions.selection.unselect'),
            icon: 'fa-solid fa-times',
            command: () => unselectAll(),
          });
        }

        return items;
      }),

      onHeaderCheckboxClick,
      isRowSelected,
      toggleRowSelection,
      onRowSelectionUpdate,
      toggle,
      tableClasses,

      // Wrap grouped actions to inject selection info + add export action if exportable
      groupedActions: computed(() => {
        const actions: any[] = [];

        // Add user-defined grouped actions
        if (props.groupedActionsOptions) {
          props.groupedActionsOptions.forEach((action) => {
            actions.push({
              label: action.label,
              icon: action.icon,
              disabled: action.disabled,
              command: () => {
                // Create selection info object with filters for backend processing
                const selectionInfo = {
                  selectAllMode: selectAllMode.value,
                  selectedRows: selectedRows.value,
                  excludedRows: excludedRows.value,
                  totalRecords: totalRecords.value,
                  selectionCount: selectionCount.value,
                  // Include active filters - needed by backend when selectAllMode is true
                  filters: apiFilters.value,
                };
                // Call the original command with selection info
                action.command(selectionInfo);
              },
            });
          });
        }

        // Add export selection action if exportable
        if (props.exportable) {
          actions.push({
            label: t('spmTableApi.export.selection'),
            icon: 'fa-solid fa-download',
            command: () => handleExportSelection(),
          });
        }

        return actions;
      }),

      // Export functions
      handleExportAll,
      handleExportSelection,
      showExportConfirmDialog,
      confirmExportAll,
    };
  },
});
